import {
  MutationCache,
  QueryCache,
  QueryClient,
  type DefaultOptions,
  type Query,
} from "@tanstack/react-query";

import { ApiError } from "./request";

export type QueryKeyPart = unknown;

/** A query cache belongs to the door that produced its bytes and to exactly one scope. */
export interface DoorQueryKeys<DoorId extends string> {
  global: (
    ...parts: readonly QueryKeyPart[]
  ) => readonly [DoorId, "global", ...QueryKeyPart[]];
  principal: (
    principalId: string,
    ...parts: readonly QueryKeyPart[]
  ) => readonly [DoorId, "principal", string, ...QueryKeyPart[]];
  project: (
    projectId: string,
    ...parts: readonly QueryKeyPart[]
  ) => readonly [DoorId, "project", string, ...QueryKeyPart[]];
}

export function doorQueryKeys<DoorId extends string>(
  door: DoorId,
): DoorQueryKeys<DoorId> {
  return {
    global: (...parts) => [door, "global", ...parts],
    principal: (principalId, ...parts) => [
      door,
      "principal",
      principalId,
      ...parts,
    ],
    project: (projectId, ...parts) => [door, "project", projectId, ...parts],
  };
}

export const isNetworkError = (error: unknown): boolean =>
  error instanceof TypeError;

export const isGatewayError = (error: unknown): boolean =>
  error instanceof ApiError && [502, 503, 504].includes(error.status);

/** A door's own refusal that says it may be repeated. The wire form is
 * `RefusalDetail.retryable` on a Connect refusal; the console's Connect boundary
 * surfaces it as this witness on the thrown error so this package, which does not
 * speak Connect, still reads one recovery vocabulary. */
export interface RetryableRefusal {
  readonly retryable: true;
}

export const isRetryableRefusal = (error: unknown): error is RetryableRefusal =>
  error instanceof Error &&
  (error as Partial<RetryableRefusal>).retryable === true;

/** A read may be repeated after any server failure except a response that says the
 * operation is not implemented or the protocol is unsupported, and after any refusal
 * the door itself marked retryable. Writes keep the narrower gateway-only rule above
 * because a 500 does not prove that a mutation was unseen. */
export const isTransientQueryError = (error: unknown): boolean =>
  isNetworkError(error) ||
  isRetryableRefusal(error) ||
  (error instanceof ApiError &&
    error.status >= 500 &&
    error.status <= 599 &&
    ![501, 505].includes(error.status));

/** Retain cached read bytes only across a failure expected to repair itself.
 * A permanent refusal can mean access was revoked, so its old snapshot must not
 * remain visible merely because the query cache still owns it. */
export function retainedQueryData<T>(query: {
  readonly data: T | undefined;
  readonly error: unknown;
  readonly isError: boolean;
}): T | undefined {
  if (query.data === undefined) return undefined;
  return !query.isError || isTransientQueryError(query.error)
    ? query.data
    : undefined;
}

/** One repair ramp, given its delays. Two ramps exist below and they differ for one
 * reason, so the mechanism is written once and the reason is written twice. */
const ramp =
  (delays: readonly number[]) =>
  (attempt: number): number =>
    delays[Math.min(Math.max(attempt, 0), delays.length - 1)];

/** A write's repair ramp. Nobody is watching a retried write, and the door that
 * refused it may be shedding load, so the repairs are spread out. */
export const backoffDelay = ramp([1_000, 3_000, 8_000]);

/** A read's repair ramp, and it is deliberately faster: a person is looking at the
 * region this read fills, so both repairs must land inside the pause they will sit
 * through anyway. A dropped packet costs about a second, not the fifteen the
 * background recovery interval below would have cost. */
export const readRepairDelay = ramp([250, 1_000]);

/** How many times a transient read repairs itself before the failure is shown.
 * Two, because the third attempt is what the background recovery interval is for. */
export const READ_REPAIRS = 2;

/** The soonest a mounted read repairs itself in the background, after its two fast
 *  repairs are spent. Exported because a browser suite driving a *deployed* console
 *  has to stay under it: an assertion allowed to wait this long can be satisfied by
 *  the product healing a door that was down when the test asked, which is the door
 *  being broken and the test being green. `platform/console/e2e/live-session.ts`
 *  derives the one wait both deployed-target configs use from this number. */
export const TRANSIENT_RECOVERY_FLOOR_MS = 15_000;
const TRANSIENT_RECOVERY_JITTER_MS = 15_000;

/** The freshness every read gets unless it says otherwise.
 *
 * Thirty seconds is what makes a navigation free: the console is one origin with one
 * cache, so returning to a workspace inside half a minute must paint from memory and
 * revalidate behind the paint rather than fetching from cold. It was `0`, which meant
 * every mount of every one of two hundred reads was a network round trip. */
const DEFAULT_STALE_MS = 30_000;

/** Opt-in retry for mutation variables that own one stable idempotency key. */
export interface IdempotentMutationVariables {
  readonly idempotencyKey: string;
}

export interface IdempotentMutationOptions<
  Variables extends IdempotentMutationVariables,
> {
  readonly retry: (failureCount: number, error: Error) => boolean;
  readonly retryDelay: typeof backoffDelay;
  /** Type-only witness: callers cannot select this policy for variables without a stable key. */
  readonly variables?: Variables;
}

export function idempotentMutationPolicy<
  Variables extends IdempotentMutationVariables,
>(): IdempotentMutationOptions<Variables> {
  return {
    retry: (count, error) =>
      (isNetworkError(error) || isGatewayError(error)) && count < 3,
    retryDelay: backoffDelay,
  };
}

const THIRTY_MINUTES_MS = 30 * 60_000;

/** A read the persisted cache must never write to disk. The persister reads this off
 * `query.meta`, which is the only thing about a query it can see at save time. */
export const NOT_PERSISTED = { persist: false } as const;

/** Named freshness policies. Call sites select semantics instead of restating timers.
 *
 * `reference` IS the default now — it is spelled out here only because a call site that
 * says `reference` is making a claim about its data ("changes out of band, a stale half
 * minute is fine") rather than accepting whatever the client happens to default to. */
export const queryPolicies = {
  streamHydration: {
    staleTime: Number.POSITIVE_INFINITY,
    // The owner cursor journal becomes authoritative after this one hydration read.
    // Focus and reconnect repair the stream, not the historical snapshot query.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Never persisted: a hydration snapshot is only true at the cursor it was read
    // at, and a snapshot restored from disk would hand the journal a false origin.
    meta: NOT_PERSISTED,
  },
  immutable: {
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: THIRTY_MINUTES_MS,
    // Successful immutable data is never stale, so these only repair a read
    // that has not produced its first snapshot yet.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  },
  reference: {
    staleTime: DEFAULT_STALE_MS,
    // The default: a focus is not news about data that changes out of band, and two
    // hundred mounted reads refiring on every alt-tab was the console's own load.
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  },
  operational: {
    staleTime: 0,
    // The one policy that still refetches on focus, and the reason is the claim it
    // makes: this data is live, so a person who just came back to the tab must not
    // read a number that stopped being true while they were away. `true` rather than
    // `"always"` because a zero stale time already makes every focus a refetch —
    // `"always"` said the same thing twice and also overrode a call site's own
    // `staleTime` when one was layered on top.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  },
  sessionStable: (ttlMs: number) => ({
    staleTime: ttlMs,
    gcTime: ttlMs,
    // A fresh snapshot still does not churn. Once it is stale or failed, connectivity
    // transitions are recovery signals rather than dead ends.
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  }),
} as const;

/** The floor for a poll a person is watching.
 *
 * Below five seconds nobody can see the difference and the door pays for it several times
 * over: the console had reads at two and three seconds on six pages, which is twenty to
 * thirty requests a minute per open page to learn that nothing had changed. Five seconds
 * is the shortest interval that still reads as live. The one thing that is allowed to go
 * faster is a log tail, which is a stream wearing a poll's clothes. */
export const LIVE_POLL_MS = 5_000;

/** refetchInterval helper for "poll while something is still running, stop otherwise". */
export function pollWhile<T>(
  active: (data: T) => boolean,
  intervalMs: number,
): (query: {
  // Not optional, and not `unknown`: this shape is what react-query infers a query's
  // error type *from* when the function is assigned to `refetchInterval`, so `unknown`
  // widened every polled read's `.error` at its call site and `?` added `undefined` to
  // it. A query always has the field; `null` is how it says there is no error.
  state: { data: T | undefined; error: Error | null };
}) => number | false {
  return (query) => {
    if (isTransientQueryError(query.state.error)) return intervalMs;
    const data = query.state.data;
    return data !== undefined && active(data) ? intervalMs : false;
  };
}

/** One failed read or write, as the thing that records it sees it.
 *
 * A read reports once per *episode*, and an episode is the whole repair sequence: the
 * two immediate retries below and every background recovery after them are the same
 * failure still failing, so a read that drops one packet and recovers reports nothing
 * at all, and a real outage reports once rather than once per query per fifteen
 * seconds. A recovery closes the episode. A write reports every failure, because every
 * attempt is a person's own action. */
export interface FailureReport {
  readonly kind: "read" | "write";
  readonly error: unknown;
  /** The query or mutation key; door-keyed reads put the door at index 0. */
  readonly key: readonly unknown[];
  /** A read that failed while still showing a successful snapshot. */
  readonly hadSnapshot: boolean;
  readonly transient: boolean;
}

export type FailureObserver = (failure: FailureReport) => void;

/** Reports failures to `observe` at the cache, so nothing a page renders decides
 * whether a failure is recorded. An error state that forgets to report is the
 * defect this shape removes: the only way for a failed read to go unrecorded is for
 * it not to happen. */
function observedCaches(observe: FailureObserver): {
  queryCache: QueryCache;
  mutationCache: MutationCache;
} {
  const failing = new WeakSet<Query<unknown, unknown, unknown, readonly unknown[]>>();
  return {
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (failing.has(query)) return;
        failing.add(query);
        observe({
          kind: "read",
          error,
          key: query.queryKey,
          hadSnapshot: query.state.data !== undefined,
          transient: isTransientQueryError(error),
        });
      },
      onSuccess: (_data, query) => {
        failing.delete(query);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        observe({
          kind: "write",
          error,
          key: mutation.options.mutationKey ?? [],
          hadSnapshot: false,
          transient: isTransientQueryError(error),
        });
      },
    }),
  };
}

export function makeQueryClient(
  overrides: DefaultOptions = {},
  observe?: FailureObserver,
): QueryClient {
  // One interval per browser client keeps a recovered door from receiving every
  // mounted query at the same instant after a shared outage.
  const transientRecoveryIntervalMs =
    TRANSIENT_RECOVERY_FLOOR_MS +
    Math.floor(Math.random() * TRANSIENT_RECOVERY_JITTER_MS);
  return new QueryClient({
    ...(observe === undefined ? {} : observedCaches(observe)),
    defaultOptions: {
      ...overrides,
      queries: {
        staleTime: DEFAULT_STALE_MS,
        // A tab regaining focus is not news about the data. It was `true`, and with a
        // zero stale time that made every alt-tab refetch every mounted read on the
        // page. `operational` opts back in for the reads that are genuinely live.
        refetchOnWindowFocus: false,
        // Reconnecting is news: the browser was offline, so anything on screen may
        // have been read before the gap.
        refetchOnReconnect: true,
        // A poll is for a person watching it. A hidden tab polling a door is load
        // nobody is reading; the interval resumes when the tab is looked at again.
        refetchIntervalInBackground: false,
        // A transient read repairs itself twice, fast, before anyone is told. One
        // dropped packet is not an outage, and the console used to render it as one
        // for up to thirty seconds — the background interval below was the only
        // repair. Permanent refusals (4xx, 501, 505) still surface on the first
        // answer, because repeating them would only delay the truth.
        retry: (failureCount, error) =>
          isTransientQueryError(error) && failureCount < READ_REPAIRS,
        retryDelay: readRepairDelay,
        // After the two fast repairs, a mounted read keeps repairing itself in the
        // background so a door coming back needs no reload and no Try again.
        refetchInterval: (query) =>
          isTransientQueryError(query.state.error)
            ? transientRecoveryIntervalMs
            : false,
        ...overrides.queries,
      },
      mutations: {
        ...overrides.mutations,
        // Retrying a write is unsafe unless its call site explicitly opts in above.
        retry: false,
      },
    },
  });
}
